import React from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SearchWidget } from "@/components/SearchWidget";
import { useI18n } from "@/context/i18n";
import { ShieldCheck, BadgeCheck, Headphones, Wallet, ArrowRight, Car, Ticket, Construction } from "lucide-react";

const HERO = "https://images.pexels.com/photos/14387344/pexels-photo-14387344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const ANTALYA = "https://images.pexels.com/photos/24807133/pexels-photo-24807133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CAPPADOCIA = "https://images.pexels.com/photos/8207003/pexels-photo-8207003.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ACTIVITY = "https://images.pexels.com/photos/9896355/pexels-photo-9896355.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TRANSFER = "https://images.pexels.com/photos/18029638/pexels-photo-18029638.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ISTANBUL = "https://images.unsplash.com/photo-1621873129944-8d88a5328a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const DESTS = [
  { code: "IST", slug: "istanbul", name: "Istanbul", img: ISTANBUL },
  { code: "AYT", slug: "antalya", name: "Antalya", img: ANTALYA },
  { code: "CAP", slug: "cappadocia", name: "Cappadocia", img: CAPPADOCIA },
  { code: "FET", slug: "fethiye", name: "Fethiye & Ölüdeniz", img: "https://images.pexels.com/photos/37426500/pexels-photo-37426500.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

export default function Home() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="home-page">
      <Header />

      {/* HERO */}
      <section className="relative pt-[68px]">
        <div className="absolute inset-0 h-[500px] sm:h-[520px]">
          <img src={HERO} alt="Luxury resort in Türkiye" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B2038]/45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2038] via-[#0B2038]/75 to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 pt-14 pb-10">
          <div data-testid="site-building-banner" className="mb-6 flex items-center justify-center gap-2 rounded-full bg-amber-400 text-[#0B2038] text-sm font-bold px-4 py-2 shadow-lg max-w-max mx-auto">
            <Construction className="w-4 h-4" />
            Under construction
          </div>
          <div className="max-w-2xl fade-up">
            <span className="label-caps text-[#E3BE4F]">{t("tagline")}</span>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mt-3">{t("hero_title")}</h1>
            <p className="text-white/80 text-sm md:text-base mt-4 max-w-lg">{t("hero_sub")}</p>
          </div>
          <div className="mt-9 fade-up" style={{ animationDelay: ".12s" }}><SearchWidget /></div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[[BadgeCheck, t("trust_1_t"), t("trust_1_d")], [Wallet, t("trust_2_t"), t("trust_2_d")],
            [ShieldCheck, t("trust_3_t"), t("trust_3_d")], [Headphones, t("trust_4_t"), t("trust_4_d")]].map(([Icon, ti, d], i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex items-start gap-3">
              <Icon className="w-6 h-6 text-[#009E9E] shrink-0" strokeWidth={1.8} />
              <div>
                <p className="font-bold text-[#0B2038] text-sm leading-tight">{ti}</p>
                <p className="text-[#475569] text-xs mt-1 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* POPULAR DESTINATIONS */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="label-caps">{t("popular")}</span>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B2038] mt-1">{t("popular_dests")}</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {DESTS.map((d) => (
            <Link key={d.code} to={`/region/${d.slug}`} data-testid={`dest-${d.code}`}
              className="relative rounded-2xl overflow-hidden h-56 group card-lift block">
              <img src={d.img} alt={d.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2038]/85 via-[#0B2038]/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <p className="font-display text-xl font-extrabold">{d.name}</p>
                <p className="text-xs text-white/75 flex items-center gap-1 mt-0.5">{t("view_hotels")} <ArrowRight className="w-3.5 h-3.5" /></p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PRODUCT PROMOS */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-14">
        <div className="grid md:grid-cols-2 gap-4">
          {[{ img: TRANSFER, Icon: Car, ti: t("promo_transfer_t"), d: t("promo_transfer_d"), to: "/results/transfers?destination=IST", id: "promo-transfers" },
            { img: ACTIVITY, Icon: Ticket, ti: t("promo_activity_t"), d: t("promo_activity_d"), to: "/results/activities?destination=IST", id: "promo-activities" }].map((p) => (
            <Link key={p.id} to={p.to} data-testid={p.id}
              className="relative rounded-2xl overflow-hidden h-64 group card-lift block">
              <img src={p.img} alt={p.ti} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B2038] via-[#0B2038]/85 to-[#0B2038]/25" />
              <div className="absolute inset-0 p-7 flex flex-col justify-center max-w-sm">
                <p.Icon className="w-7 h-7 text-[#C9A227] mb-3" strokeWidth={1.7} />
                <p className="font-display text-2xl font-extrabold text-white leading-tight">{p.ti}</p>
                <p className="text-white/75 text-sm mt-2">{p.d}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-white">
                  {t("explore")} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
